/**
 * Menu Maker Tabs Navigation Component
 * Displays navigation tabs for different menu maker sections
 *
 * @module components/MenuMakerTabsNavigation
 */
import PropTypes from 'prop-types';
import { WithTranslation, withTranslation } from 'react-i18next';
import { ContentLayout } from 'Layout';
import useUserStore from 'hooks/useUserStore';
import { MENU_ROUTES } from 'utils/menuMaker/constants';

import useStyles from './styles';

/**
 * Props for MenuMakerTabsNavigation component
 *
 * @interface MenuMakerTabsNavigationProps
 */
interface MenuMakerTabsNavigationProps extends WithTranslation {
  /** Translation function from react-i18next */
  t: WithTranslation['t'];
}

/**
 * Menu Maker Tabs Navigation Component
 * Renders navigation tabs for different sections of the menu maker module
 *
 * Features:
 * - Conditional publish tab based on draft mode
 * - Responsive design with proper accessibility
 *
 * @param {MenuMakerTabsNavigationProps} props - Component props
 * @returns {JSX.Element} Tabs navigation component
 *
 * @example
 * <MenuMakerTabsNavigation />
 */
function MenuMakerTabsNavigation({ t }: MenuMakerTabsNavigationProps): JSX.Element {
  const classes = useStyles();

  const {
    storeState: { selected: storeSelected },
  } = useUserStore();

  return (
    <div className={classes.root}>
      <ContentLayout.TabsNavigation>
        <ContentLayout.TabLink label={t('menuMaker:tabs.menusResume')} to={MENU_ROUTES.RESUME} />
        <ContentLayout.TabLink label={t('menuMaker:tabs.menus')} to={MENU_ROUTES.MENUS} />
        <ContentLayout.TabLink label={t('menuMaker:tabs.categories')} to={MENU_ROUTES.CATEGORIES} />
        <ContentLayout.TabLink label={t('menuMaker:tabs.articles')} to={MENU_ROUTES.PRODUCTS} />
        <ContentLayout.TabLink label={t('menuMaker:tabs.customizableGroups')} to={MENU_ROUTES.MODIFIER_GROUPS} />
        {!storeSelected?.isDraft && (
          <ContentLayout.TabLink label={t('menuMaker:tabs.publish')} to={MENU_ROUTES.PUBLISH} />
        )}
      </ContentLayout.TabsNavigation>
    </div>
  );
}

MenuMakerTabsNavigation.propTypes = {
  t: PropTypes.func,
};

export default withTranslation('menuMaker')(MenuMakerTabsNavigation);
